import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import theme from './assets/styles/theme';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Breakdowns from './pages/Breakdowns';
import BreakdownDetails from './pages/BreakdownDetails';
import Events from './pages/Events';
import EventDetails from './pages/EventDetails';
import EventWizard from './pages/EventWizard';
import UserProfile from './pages/UserProfile';
import About from './pages/About';
import BreakdownWizard from './pages/BreakdownWizard';
import BreakdownRequests from './pages/BreakdownRequests';
import AccidentReports from './pages/AccidentReports';
import AccidentReportDrafts from './pages/AccidentReportDrafts';
import AccidentReportNew from './pages/AccidentReportNew';
import Inspections from './pages/Inspections';
import NewInspection from './pages/NewInspection';
import DraftInspection from './pages/DraftInspection';
import InspectionTemplateDetail from './pages/InspectionTemplateDetail';
import InspectionResponseDetail from './pages/InspectionResponseDetail';
import NewInspections from './pages/NewInspections';
import InspectionsInProgress from './pages/InspectionsInProgress';
import MainLayout from './layouts/MainLayout';
import DesktopDetection from './components/DesktopDetection';
import InstallPWAPrompt from './components/InstallPWAPrompt';
import UpdateAvailablePrompt from './components/UpdateAvailablePrompt.jsx';
import { hasValidToken } from './utils/tokenUtils';
import './App.css';
import { getUserRole } from './utils/tokenUtils';
import Map from './pages/Map.jsx';
import RouteWatcher from './utils/routerWatcher';
import Documents from './pages/Documents.jsx';
import DocumentsInProgress from './pages/DocumentsInProgress.jsx';
import DocumentFill from './pages/DocumentFill.jsx';
import { refreshAccess } from './utils/accessStore';
import { hasPermission, CREATE_DOCUMENT_PERMISSION, NEW_INSPECTIONS_PERMISSION } from './utils/features';
// Protected Route Component
const ProtectedRoute = ({ children }) => {
  if (!hasValidToken()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const RoleProtectedRoute = ({ allowedRoles, children }) => {
  const role = String(getUserRole() || '').toUpperCase();
  if (!allowedRoles.includes(role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PermissionProtectedRoute = ({ permission, children }) => {
  const permissions = Array.isArray(permission) ? permission : [permission];
  if (!permissions.some((key) => hasPermission(key))) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  const userRole = getUserRole();

  // Users returning with a live session never pass through Login, so this is
  // the only place their permissions get refreshed. Failures fall back to the
  // cached or token-derived set rather than locking anything down.
  useEffect(() => {
    if (hasValidToken()) refreshAccess();
  }, []);

  return (
    <ConfigProvider theme={theme}>
      <Router>
        {/* Desktop Detection Component */}
        <RouteWatcher />
        <DesktopDetection />

        {/* PWA Install Prompt */}
        <InstallPWAPrompt />

        {/* SW Update Prompt */}
        <UpdateAvailablePrompt />

        <Routes>
          {/* Public Route */}
          <Route path="/" element={<Login />} />

          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            {/* Default route redirects to dashboard */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/breakdowns" element={<Breakdowns userRole={userRole} />} />
            <Route path="/breakdown/:id" element={<BreakdownDetails />} />
            <Route path="/breakdown/:id/notes" element={<BreakdownDetails />} />
            <Route path="/events" element={<Events />} />
            <Route
              path="/events/new"
              element={
                <RoleProtectedRoute allowedRoles={['CONSULTANT', 'OWNER', 'SUPPLIER']}>
                  <EventWizard />
                </RoleProtectedRoute>
              }
            />
            <Route path="/events/:id" element={<EventDetails />} />
            <Route path="/events/id/:id" element={<EventDetails />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/about" element={<About />} />
            <Route path="/createBreakdown" element={<BreakdownWizard />} />
            <Route path="/breakdownRequests" element={<BreakdownRequests />} />
            <Route path="/accidentReports" element={<AccidentReports />} />
            <Route path="/accidentReports/drafts" element={<AccidentReportDrafts />} />
            <Route path="/accidentReports/new" element={<AccidentReportNew />} />
            <Route path="/inspections" element={<Inspections />} />
            <Route path="/inspections/newInspections" element={<NewInspection />} />
            <Route path="/inspections/draftInspections" element={<DraftInspection />} />
            <Route path="/inspections/templates/:templateId" element={<InspectionTemplateDetail />} />
            <Route path="/inspections/responses/:responseId" element={<InspectionResponseDetail />} />
            <Route
              path="/inspections/new"
              element={
                <PermissionProtectedRoute permission={NEW_INSPECTIONS_PERMISSION}>
                  <NewInspections />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/inspections/new/in-progress"
              element={
                <PermissionProtectedRoute permission={NEW_INSPECTIONS_PERMISSION}>
                  <InspectionsInProgress />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <PermissionProtectedRoute permission={CREATE_DOCUMENT_PERMISSION}>
                  <Documents />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/documents/in-progress"
              element={
                <PermissionProtectedRoute permission={CREATE_DOCUMENT_PERMISSION}>
                  <DocumentsInProgress />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/documents/fill/:templateId"
              element={
                // Shared with New Inspections, which fills its inspections here too.
                <PermissionProtectedRoute permission={[CREATE_DOCUMENT_PERMISSION, NEW_INSPECTIONS_PERMISSION]}>
                  <DocumentFill />
                </PermissionProtectedRoute>
              }
            />
            <Route
              path="/documents/:documentId"
              element={
                <PermissionProtectedRoute permission={[CREATE_DOCUMENT_PERMISSION, NEW_INSPECTIONS_PERMISSION]}>
                  <DocumentFill />
                </PermissionProtectedRoute>
              }
            />
            <Route path="/map" element={<Map />} />
            {/* Add more routes here as needed */}
          </Route>

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
